import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../store/store"; // Assuming your store file is named 'store.ts'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Form,
  FormInstance,
  Image,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Tag,
  Tooltip,
} from "antd";
import {
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LoginOutlined,
  PlusOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { deleteData, fetchData, postData, putData } from "../store/actions/cms";
import { useEffect, useRef, useState } from "react";
import { PATH_CMS as PATH, PATH_MANAGE } from "../config";
import CustomPaginatedTable from "../components/CustomPaginatedTable";
import PageHeader from "../components/PageHeader";
import { useLocalState } from "../hooks/useLocalStorage";

import { capitalize } from "lodash";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { render } from "@testing-library/react";
import { queryClient } from "../store/queryClient";
import { useMutation } from "@tanstack/react-query";
import { queryApi } from "../store/actions/common";
import SingleUpload from "../components/SingleUpload";
import CustomForm from "../components/CustomForm";
import { triggerNotification } from "../store/directActions";
import CustomDrawer from "../components/hoc/CustomDrawer";
import CustomFilters from "../components/CustomFilters";
import { Car, DollarSign, FileText, Fuel, Wrench } from "lucide-react";
import Icon from "../components/Icon";
import moment from "moment";

const { Option } = Select;

interface OwnProps {}

type configurationPageProps = OwnProps & PropsFromRedux;

const PAGE_TYPE = "expense";
const VIEW_MODE_STORAGE_KEY = "shared-page-view-mode";

const ConfigurationPage: React.FC<configurationPageProps> = ({
  lookUpData,
  storeId,
  fetchCms,
  createData,
  editData,
  REFRESH,
}) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [viewMode, setViewMode] = useLocalState(VIEW_MODE_STORAGE_KEY, "card");

  const [filterData, setFilterData] = useState({
    entity_id: "dim.screen.backgrounds",
  });

  const navigate = useNavigate();

  const [form] = Form.useForm();

  const updateDrawer = (state: boolean = false) => {
    setShowDrawer(state);
  };

  const handleEditClicked = (record: any) => {
    form.resetFields();
    if (record) {
      form.setFieldsValue({
        ...record,
        isFormEdit: true,
        uniId: record.provider_id,
      });
      updateDrawer(true);
    }
  };

  const {
    mutate,
    isPending: isPendingMutation,
    // isError,
    // error,
  } = useMutation({
    mutationFn: ({ data, method, path }: any) => queryApi(path, method, data),
    onSuccess: () => {
      updateDrawer();
      queryClient.invalidateQueries({
        queryKey: [PATH],
      });
    },
    onError: (a) => {
      triggerNotification({
        title: "Error",
        description: a.message,
        type: "error",
      });
    },
  });

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "Fuel":
        return <Icon name="Fuel" className="w-4 h-4" />;
      case "Maintenance":
      case "Repair":
        return <Icon name="Wrench" className="w-4 h-4" />;
      default:
        return <Icon name="DollarSign" className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Fuel":
        return "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300";
      case "Maintenance":
        return "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300";
      case "Insurance":
        return "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300";
      case "Repair":
        return "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300";
      case "Registration":
        return "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300";
      case "Parking":
        return "app-surface-alt app-muted";
      default:
        return "app-surface-alt app-muted";
    }
  };

  const currentAction = useRef<string | undefined>();

  const CMS_TYPE = "dim.screen.backgrounds";

  const onFormSubmit = (values: Record<string, any>) => {
    if (!values || !values.image_url) return;
    mutate({
      path: `${PATH_MANAGE}${PATH}`,
      method: "POST",
      data: {
        data: JSON.stringify(values.image_url),
        entity_id: CMS_TYPE,
      },
    });
  };

  const removeCMS = (cms_id: any) => {
    mutate({
      path: `${PATH_MANAGE}${PATH}/${cms_id}`,
      method: "DELETE",
    });
  };

  const columns = [
    {
      title: "Background",
      dataIndex: "data",
      key: "image_url",
      render: (data) => {
        if (!!data) {
          const background = JSON.parse(data);
          return background.filetype.includes("video") ? (
            <video height="200" width="200" controls>
              <source src={background?.url} />
              Your browser does not support the video tag.
            </video>
          ) : (
            <Image width={200} height={200} src={background?.url ?? ""} />
          );
        }
      },
    },
    {
      title: "Action",
      key: "action",
      render: (item) => (
        <Popconfirm
          title="Are you sure to delete this?"
          onConfirm={() => removeCMS(item.cms_id)}
          onCancel={() => {}}
          okText="Yes"
          cancelText="No"
        >
          <DeleteOutlined style={{ fontSize: 22, color: "red" }} />
        </Popconfirm>
      ),
    },
  ];

  const customRender = (item: any, index: number) => {
    let background: any = null;
    if (item?.data) {
      try {
        background = JSON.parse(item.data);
      } catch (e) {
        background = null;
      }
    }
    return (
      <div
        key={item.cms_id || index}
        className="app-surface rounded-xl shadow-sm border border-solid app-border p-4 hover:shadow-md transition-shadow duration-200 mb-4"
      >
        <div className="flex justify-center mb-4">
          {background ? (
            background.filetype?.includes("video") ? (
              <video height="200" width="100%" controls>
                <source src={background?.url} />
                Your browser does not support the video tag.
              </video>
            ) : (
              <Image src={background?.url ?? ""} className="rounded-lg" />
            )
          ) : (
            <div className="app-muted">No preview</div>
          )}
        </div>
        <div className="flex justify-end pt-3 border-0 border-t border-solid app-border">
          <Popconfirm
            title="Are you sure to delete this?"
            onConfirm={() => removeCMS(item.cms_id)}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            <DeleteOutlined style={{ fontSize: 20, color: "red" }} />
          </Popconfirm>
        </div>
      </div>
    );
  };

  return (
    <section id="customers" className="page-section h-page p-6 flex flex-col">
      {/* Header and Actions */}
      {/* <div className="app-surface rounded-lg shadow-sm border app-border p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h2 className="text-2xl font-bold text-[#0c1248]">
              Expense Management
            </h2>
            <p className="app-muted mt-1">
              Track and manage all vehicle-related expenses
            </p>
          </div>
          <div className="flex items-center space-x-3 mt-4 md:mt-0">
            <button
              onClick={() => {
                form.resetFields();
                updateDrawer(true);
              }}
              className="px-4 py-2 bg-[#0c1248] text-white font-medium rounded-lg hover:bg-[#0c1248]/90 transition duration-200 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Expense
            </button>
          </div>
        </div>
      </div> */}
      <PageHeader
        title="Backgrounds"
        extra={[
          <Button
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              updateDrawer(true);
            }}
          >
            Add
          </Button>,
          <Button.Group key="view-toggle">
            <Button
              type={viewMode === "table" ? "primary" : "default"}
              icon={<TableOutlined />}
              onClick={() => setViewMode("table")}
            >
              Table
            </Button>
            <Button
              type={viewMode === "card" ? "primary" : "default"}
              icon={<AppstoreOutlined />}
              onClick={() => setViewMode("card")}
            >
              Card
            </Button>
          </Button.Group>,
        ]}
      />

      {/* Stats Cards */}
      {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="app-surface rounded-lg shadow-sm p-6 border app-border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium app-muted">
                Total Expenses
              </p>
              <p className="text-2xl font-bold app-text">${"1000"}</p>
            </div>
          </div>
        </div>
        <div className="app-surface rounded-lg shadow-sm p-6 border app-border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Fuel className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium app-muted">Fuel Expenses</p>
              <p className="text-2xl font-bold app-text">$ 0</p>
            </div>
          </div>
        </div>
        <div className="app-surface rounded-lg shadow-sm p-6 border app-border">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Wrench className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium app-muted">Maintenance</p>
              <p className="text-2xl font-bold app-text">$ 0</p>
            </div>
          </div>
        </div>
        <div className="app-surface rounded-lg shadow-sm p-6 border app-border">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <FileText className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium app-muted">Total Records</p>
              <p className="text-2xl font-bold app-text">0</p>
            </div>
          </div>
        </div>
      </div> */}

      {/* Controls */}
      {/* <div className="app-surface rounded-lg shadow-sm p-6 mb-6 border app-border">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 app-muted" />
              <input
                type="text"
                placeholder="Search expenses..."
                className="pl-10 pr-4 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="relative">
              <Filter className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 app-muted" />
              <select
                className="pl-10 pr-8 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none app-surface"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div> */}

      {/* <div className="app-surface rounded-lg shadow-sm border app-border p-6 mb-6">
        <CustomFilters
          // @ts-ignore
          setFilterData={setFilterData}
          table="expense"
          dateRangePicker
          dateRangeField="created_at"
          formData={[
            {
              title: "name",
              display: "Description",
              placeholder: "description",
              inputType: "text",
              required: true,
              // inputType: "attribute",
              // collectionName: "make",
              // relationType: "single",
            },
            {
              title: "cost",
              display: "Expense",
              inputType: "number",
            },
            {
              title: "expense_type",
              placeholder: "entity",
              inputType: "attribute",
              collectionName: "expenseType",
              required: true,
              relationType: "single",
            },
            {
              title: "entity_type",
              placeholder: "entity",
              inputType: "attribute",
              collectionName: "entityType",
              required: true,
              relationType: "single",
            },
            {
              title: "entity_reason",
              placeholder: "Reason",
              inputType: "attribute",
              collectionName: "entityType.reason",
              required: true,
              relationType: "single",
              dependsOn: "entity_type",
            },

            {
              title: "entity_id",
              display: "Car",
              placeholder: "select car",
              inputType: "relation",
              collectionName: "car",
              required: true,
              visibility_depends_on: "entity_type",
              visibility_condition: true,
              visibility_value: ["Car"],
            },
            {
              title: "entity_id",
              display: "Store",
              placeholder: "select store",
              inputType: "relation",
              collectionName: "store",
              required: true,
              visibility_depends_on: "entity_type",
              visibility_condition: true,
              visibility_value: ["Store"],
            },
            {
              title: "entity_id",
              display: "Employee",
              placeholder: "select employee",
              inputType: "relation",
              collectionName: "employee",
              required: true,
              visibility_depends_on: "entity_type",
              visibility_condition: true,
              visibility_value: ["Employee"],
            },
            // {
            //   title: "variant",
            //   placeholder: "variant",
            //   inputType: "text",
            //   // collectionName: "make.model.variant",
            //   required: true,
            //   // relationType: "single",
            //   // dependsOn: "model",
            // },
            // {
            //   title: "description",
            //   inputType: "textarea",
            // },
            // {
            //   title: "color",
            //   inputType: "text",
            // },
            // {
            //   title: "registration_number",
            //   display: "number",
            //   inputType: "text",
            // },
            // {
            //   title: "transmission",
            //   inputType: "attribute",
            //   collectionName: "transmission",
            //   required: true,
            //   relationType: "single",
            // },
            // {
            //   title: "car_type",
            //   inputType: "attribute",
            //   collectionName: "carType",
            //   required: true,
            //   relationType: "single",
            // },
            // {
            //   title: "fuel_type",
            //   inputType: "attribute",
            //   collectionName: "fuelType",
            //   required: true,
            //   relationType: "single",
            // },
            // {
            //   title: "driven",
            //   inputType: "number",
            // },
            // {
            //   title: "year",
            //   display: "Year of registration",
            //   inputType: "number",
            // },
            // {
            //   title: "cost_price",
            //   inputType: "number",
            // },
            // {
            //   title: "selling_price",
            //   inputType: "number",
            // },
          ]}
        />
      </div> */}
      {/* @ts-ignore */}
      <CustomPaginatedTable
        columns={columns}
        showLimitChanger
        filterData={filterData}
        queryKey={[PATH, { type: "all" }]}
        path={`${PATH_MANAGE}${PATH}`}
        customRender={viewMode === "card" ? customRender : null}
        customWrapperClass={
          viewMode === "card"
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : undefined
        }
        tableProps={
          viewMode === "table"
            ? {
                sticky: true,
                tableLayout: "auto",
                scroll: { y: true, x: true },
              }
            : undefined
        }
      />

      <CustomDrawer
        title="Upload background"
        width={600}
        onClose={() => updateDrawer()}
        open={showDrawer}
        maskClosable={false}
        bodyStyle={{ paddingBottom: 80 }}
        footer={
          <div
            style={{
              textAlign: "right",
            }}
          >
            <Button
              onClick={() => updateDrawer()}
              style={{ marginRight: 8 }}
              disabled={isPendingMutation}
            >
              Cancel
            </Button>
            <Button
              loading={isPendingMutation}
              onClick={() => {
                form.submit();
              }}
              type="primary"
            >
              Submit
            </Button>
          </div>
        }
      >
        <CustomForm
          form={form}
          onFormSubmit={onFormSubmit}
          formData={[
            {
              title: "image_url",
              display: "Background",
              placeholder: "make",
              inputType: "media",
              required: true,
            },
          ]}
        />
      </CustomDrawer>

      {/* Expenses Table */}
      {/* <div className="app-surface rounded-lg shadow-sm border app-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="app-surface-alt border-b app-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Vehicle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Receipt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="app-surface divide-y divide-gray-200">
              {expenses.map((expense) => (
                <tr key={expense.id} className="app-hover">
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-text">
                    {new Date(expense.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-text">
                    <div className="flex items-center gap-2">
                      <Car className="w-4 h-4 app-muted" />
                      {expense.vehicle}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(
                        expense.category
                      )}`}
                    >
                      {getCategoryIcon(expense.category)}
                      {expense.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm app-text max-w-xs truncate">
                    {expense.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold app-text">
                    ${expense.amount.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-muted">
                    {expense.receipt ? (
                      <span className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer">
                        View Receipt
                      </span>
                    ) : (
                      <span className="app-muted">No receipt</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-muted">
                    <div className="flex items-center gap-2">
                      <button
                        // onClick={() => handleEdit(expense)}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                      >
                        <Icon name="Edit2" className="w-4 h-4" />
                      </button>
                      <button
                        // onClick={() => handleDelete(expense.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                      >
                        <Icon name="Trash2" className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> */}

      {/* Add/Edit Modal */}
      {/* {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="app-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold app-text mb-4">
                {editingExpense ? "Edit Expense" : "Add New Expense"}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Vehicle
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.vehicle}
                    onChange={(e) =>
                      setFormData({ ...formData, vehicle: e.target.value })
                    }
                  >
                    <option value="">Select Vehicle</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle} value={vehicle}>
                        {vehicle}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Category
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Enter expense description"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Amount ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    placeholder="0.00"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Receipt (optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Receipt filename or reference"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.receipt}
                    onChange={(e) =>
                      setFormData({ ...formData, receipt: e.target.value })
                    }
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 app-text app-surface-alt border app-border rounded-lg app-hover transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingExpense ? "Update" : "Add"} Expense
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  // Map state properties to props here if needed
  lookUpData: state.cms?.lookup?.country?.[0]?.children ?? [],
  storeId: state.user?.profileData?.storeId,
  REFRESH: state.levent?.[PAGE_TYPE],
});

const mapDispatchToProps = (dispatch: any) => ({
  // login: (data: any) => dispatch(getProfile(data)),
  fetchCms: (reqData: any) =>
    dispatch(fetchData(PATH_MANAGE, PATH, reqData, PAGE_TYPE)),
  generateToken: (id: any) =>
    dispatch(fetchData(PATH_MANAGE, `${PATH}/login/app/${id}`, {}, "")),
  createData: (reqData: any) =>
    dispatch(postData(PATH_MANAGE, `${PATH}`, reqData, PAGE_TYPE)),

  editData: (id: string, reqData: any) =>
    dispatch(putData(PATH_MANAGE, `${PATH}/`, id, reqData, PAGE_TYPE)),
  removeCms: (reqData: any) =>
    dispatch(deleteData(PATH_MANAGE, `${PATH}/`, reqData, PAGE_TYPE)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

// Define the props that connect will inject into the component
type PropsFromRedux = ConnectedProps<typeof connector>;

// Connect the component to Redux
export default connector(ConfigurationPage);
